import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Save, X, Image as ImageIcon, Upload, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

export const CollectionsManager = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', count: 0 });
  const [uploadingId, setUploadingId] = useState(null);
  const [pickerOpenForId, setPickerOpenForId] = useState(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', description: '', count: 0, image: '' });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [catRes, prodRes] = await Promise.all([
        axios.get(`${API}/admin/categories`, { headers: authHeader() }),
        axios.get(`${API}/admin/products`, { headers: authHeader() }),
      ]);
      setCategories(catRes.data);
      setProducts(prodRes.data);
    } catch (e) {
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditForm({ name: cat.name, description: cat.description || '', count: cat.count || 0 });
  };

  const saveEdit = async (id) => {
    try {
      const res = await axios.put(`${API}/admin/categories/${id}`, editForm, { headers: authHeader() });
      setCategories(categories.map(c => c.id === id ? res.data.category : c));
      setEditingId(null);
      toast.success('Collection updated');
    } catch (e) {
      toast.error('Failed to update collection');
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Remove this collection from the homepage?')) return;
    try {
      await axios.delete(`${API}/admin/categories/${id}`, { headers: authHeader() });
      setCategories(categories.filter(c => c.id !== id));
      toast.success('Collection removed');
    } catch (e) {
      toast.error('Failed to remove collection');
    }
  };

  const handleUpload = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(id);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await axios.post(
        `${API}/admin/categories/${id}/upload-image`,
        { image: base64 },
        { headers: authHeader() }
      );
      setCategories(categories.map(c => c.id === id ? { ...c, image: res.data.image } : c));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingId(null);
      e.target.value = '';
    }
  };

  const pickProductImage = async (categoryId, imageUrl) => {
    try {
      const res = await axios.put(
        `${API}/admin/categories/${categoryId}`,
        { image: imageUrl },
        { headers: authHeader() }
      );
      setCategories(categories.map(c => c.id === categoryId ? res.data.category : c));
      setPickerOpenForId(null);
      toast.success('Image updated from product');
    } catch (e) {
      toast.error('Failed to set image');
    }
  };

  const createCategory = async () => {
    if (!newForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/categories`, newForm, { headers: authHeader() });
      setCategories([...categories, res.data.category]);
      setAdding(false);
      setNewForm({ name: '', description: '', count: 0, image: '' });
      toast.success('Collection added');
    } catch (e) {
      toast.error('Failed to create collection');
    }
  };

  const filteredProducts = products.filter(p => {
    const q = pickerSearch.toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <Card className="bg-white border-none shadow-lg p-8 text-center">
        <p className="text-[#8B7D6B] font-montserrat">Loading collections...</p>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-none shadow-lg overflow-hidden" data-testid="collections-manager">
      <div className="p-6 border-b border-[#F5F3F0] flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-seasons text-[#2C2C2C]">Shop By Collection</h2>
          <p className="text-sm font-montserrat text-[#8B7D6B] mt-1">
            Manage the homepage "Shop By Collection" section. Upload images or pick from your product inventory.
          </p>
        </div>
        <Button
          onClick={() => setAdding(true)}
          data-testid="add-collection-btn"
          className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Collection
        </Button>
      </div>

      {/* Add new collection card */}
      {adding && (
        <div className="p-6 border-b border-[#F5F3F0] bg-[#FAF9F7]" data-testid="new-collection-form">
          <div className="grid md:grid-cols-4 gap-4">
            <Input
              placeholder="Collection name (e.g. Silk Collection)"
              value={newForm.name}
              onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
              data-testid="new-collection-name"
            />
            <Input
              placeholder="Description"
              value={newForm.description}
              onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
              data-testid="new-collection-description"
            />
            <Input
              type="number"
              placeholder="Designs count"
              value={newForm.count}
              onChange={(e) => setNewForm({ ...newForm, count: parseInt(e.target.value) || 0 })}
              data-testid="new-collection-count"
            />
            <div className="flex gap-2">
              <Button onClick={createCategory} data-testid="save-new-collection-btn" className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white flex-1">
                <Save className="h-4 w-4 mr-1" /> Save
              </Button>
              <Button onClick={() => { setAdding(false); setNewForm({ name: '', description: '', count: 0, image: '' }); }} variant="outline">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-[#8B7D6B] mt-2 font-montserrat">
            Tip: Save first, then upload an image or pick one from product inventory.
          </p>
        </div>
      )}

      {/* Grid of collections */}
      <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.length === 0 && (
          <p className="col-span-full text-center text-[#8B7D6B] font-montserrat">
            No collections yet. Click "Add Collection" to create one.
          </p>
        )}
        {categories.map((cat) => (
          <Card key={cat.id} data-testid={`collection-card-${cat.id}`} className="overflow-hidden border border-[#E8E2D5] shadow-sm">
            <div className="relative h-48 bg-[#F5F3F0]">
              {cat.image ? (
                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="h-10 w-10 text-[#C4B5A0]" />
                </div>
              )}
              <button
                onClick={() => deleteCategory(cat.id)}
                data-testid={`delete-collection-${cat.id}`}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full h-8 w-8 flex items-center justify-center shadow hover:bg-red-600"
                title="Remove collection"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {editingId === cat.id ? (
                <>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="Name"
                    data-testid={`edit-name-${cat.id}`}
                  />
                  <Input
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    placeholder="Description"
                    data-testid={`edit-description-${cat.id}`}
                  />
                  <Input
                    type="number"
                    value={editForm.count}
                    onChange={(e) => setEditForm({ ...editForm, count: parseInt(e.target.value) || 0 })}
                    placeholder="Count"
                    data-testid={`edit-count-${cat.id}`}
                  />
                  <div className="flex gap-2">
                    <Button onClick={() => saveEdit(cat.id)} data-testid={`save-edit-${cat.id}`} size="sm" className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white flex-1">
                      <Save className="h-4 w-4 mr-1" /> Save
                    </Button>
                    <Button onClick={() => setEditingId(null)} size="sm" variant="outline">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="font-seasons text-lg text-[#2C2C2C]">{cat.name}</h3>
                  <p className="text-sm font-montserrat text-[#8B7D6B] line-clamp-2">{cat.description || 'No description'}</p>
                  <p className="text-xs font-montserrat text-[#8B7D6B]">{cat.count}+ Designs</p>
                  <Button onClick={() => startEdit(cat)} data-testid={`edit-collection-${cat.id}`} size="sm" variant="outline" className="w-full">
                    Edit Details
                  </Button>
                </>
              )}

              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[#F5F3F0]">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleUpload(cat.id, e)}
                    disabled={uploadingId === cat.id}
                    data-testid={`upload-input-${cat.id}`}
                  />
                  <div className="flex items-center justify-center gap-2 text-xs font-montserrat px-3 py-2 border border-[#C4B5A0] text-[#C4B5A0] rounded hover:bg-[#C4B5A0] hover:text-white transition-colors">
                    <Upload className="h-3 w-3" />
                    {uploadingId === cat.id ? 'Uploading...' : 'Upload'}
                  </div>
                </label>
                <Button
                  onClick={() => { setPickerOpenForId(cat.id); setPickerSearch(''); }}
                  data-testid={`pick-from-products-${cat.id}`}
                  variant="outline"
                  size="sm"
                  className="text-xs border-[#C4B5A0] text-[#C4B5A0] hover:bg-[#C4B5A0] hover:text-white"
                >
                  <ImageIcon className="h-3 w-3 mr-1" /> From Products
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Product image picker modal */}
      <Dialog open={pickerOpenForId !== null} onOpenChange={(open) => !open && setPickerOpenForId(null)}>
        <DialogContent
          className="max-w-4xl w-[95vw] max-h-[85vh] overflow-hidden p-4 sm:p-6"
          data-testid="product-image-picker"
        >
          <DialogHeader>
            <DialogTitle className="font-seasons text-xl sm:text-2xl text-[#2C2C2C]">Pick an image from your products</DialogTitle>
            <DialogDescription className="font-montserrat text-xs sm:text-sm text-[#8B7D6B]">
              Click any image below to set it as the cover for this collection.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B7D6B]" />
            <Input
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              placeholder="Search by name or category..."
              className="pl-9"
              data-testid="picker-search-input"
            />
          </div>
          <div
            className="overflow-y-auto pr-1 -mr-1"
            style={{ maxHeight: 'calc(85vh - 180px)' }}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filteredProducts.flatMap(p => {
                const imgs = (p.images && p.images.length > 0) ? p.images : (p.image ? [p.image] : []);
                return imgs.map((img, idx) => (
                  <button
                    key={`${p.id}-${idx}`}
                    type="button"
                    onClick={() => pickProductImage(pickerOpenForId, img)}
                    data-testid={`pick-product-image-${p.id}-${idx}`}
                    className="group relative block border border-[#E8E2D5] rounded overflow-hidden hover:ring-2 hover:ring-[#C4B5A0] transition-all aspect-[3/4] bg-[#F5F3F0]"
                  >
                    <img
                      src={img}
                      alt={p.name}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                      <span className="text-white text-xs font-montserrat line-clamp-2 text-left">{p.name}</span>
                    </div>
                  </button>
                ));
              })}
              {filteredProducts.length === 0 && (
                <p className="col-span-full text-center text-[#8B7D6B] font-montserrat py-8">No products match your search.</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
